import HaspPage, { HaspTemplatePage } from '../Hasp/HaspPage'
import { generateObjectYaml, generateDatasetSectionYaml } from '../utils/hassYamlGenerator.js';
import { exportFlows } from '../flow/useFlowStore.js';
import { useDatasetStore } from '../stores/datasetStore.js';
import { AUDIO_SYNTH_RESOURCE_TYPE, useAudioStore, audioPlaybackCommand } from '../stores/audioStore.js';
import { useSynthStore } from '../stores/synthStore.js';
import { synthDeviceFilename } from '../services/synthDeploy.js';
import { useModel3dStore } from '../stores/model3dStore.js';
import { serializeObjectActions, splitGestureWire } from '../utils/objectActions.js';
import { hasSchemaProp } from '../utils/resourceUsage.js';
import { getObjectSchema } from '../Hasp/properties/v2/wireFormat.js';
import { findExportObject, getExportPageContext } from './actionTargetResolver.js';
import { attachDesignerAuthoringData, attachDesignerEndpointIds, attachDesignerPageRoutingSettings, orderFirmwarePageRecords, resolveBadgeConnectionWireModel, splitFirmwareBadgeRecords, splitFirmwareConnectionRecords, stripUnsupportedObjects } from './designerCompatibility.js';
import { connectionRoutingSettings } from '../draw/connectionRoutingSettings';
import {
  enforceFirmwareLineLimit,
  firmwareRecordByteLength,
  FIRMWARE_MQTT_MAX_JSONL_PAYLOAD_BYTES,
  validateFirmwareJsonlLineLimit,
} from './firmwareLineLimit.js';
import { canonicalizeGpioBindings, validateGpioBindings } from '../gpio/GpioBindingModel.js';
import { gpioDescriptorFromConfig } from '../gpio/gpioPlateConfig.js';
import { resolveUsedH3dRuntimeFilenames } from '../services/h3dResourcePipeline.js';
import { isProjectFontReference } from '../utils/fontReference.js';
import { generateHomeAssistantAutomationYamlForLayer } from '../automation/generators/HomeAssistantAutomationGenerator.js';
import { validateDesignerObjectIds } from '../flow/designerObjectIds.js';
import { serializeTemplateDefinitionRecords } from '../flow/templateDefinition.js';
import { attachPageLogicActions } from '../logic/pageLogic.js';
import { compilePageHass, getEffectiveHassConfig } from '../hass/hassEffective.js';

function exportSnapshot(records) {
  return JSON.parse(JSON.stringify(records));
}

function mergeGpioEventBindings(existing, incoming) {
  const result = [];
  const seen = new Set();
  for (const binding of [...(existing ?? []), ...(incoming ?? [])]) {
    const key = JSON.stringify([
      binding?.event, binding?.source_property, binding?.entity, binding?.property,
      binding?.in_min, binding?.in_max, binding?.out_min, binding?.out_max,
    ]);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(binding);
  }
  return result;
}

export default class Exporter {
  constructor() {
    this.exportData = [];
    this.validationErrors = [];
    this.validationErrorCategories = [];
    // Non-blocking, unlike validationErrors — informational notices about
    // widgets silently excluded from the plate export (e.g. no Dataset linked).
    this.warnings = [];
  }

  /**
   * @param {boolean} includeHass   - include hass_config on each object
   * @param {boolean} isDesignSave  - true: include template pages (design round-trip);
   *                                  false (default): runtime JSONL with template definitions
   * @param {{templateVisibilityMode?: 'semantic'|'legacy'}} exportOptions
   *                                  legacy is an explicit compatibility mode
   */
  doExport(settings, layer, includeHass, isDesignSave = false, { templateVisibilityMode = 'semantic' } = {}) {
    this.exportData = [];
    this.validationErrors = [];
    this.validationErrorCategories = [];
    this.warnings = [];
    this.layer = layer;
    this.settings = settings;
    this.templateVisibilityMode = templateVisibilityMode;
    const objectIdErrors = validateDesignerObjectIds(layer);
    this._addValidationErrors(objectIdErrors, 'general');
    // A design save remains possible so an imported project with collisions
    // can be repaired in the Designer. Device/export artifacts are blocked;
    // they must never receive a silent runtime ID remapping.
    if (objectIdErrors.length > 0 && !isDesignSave) return [];
    const pages = [];
    // The Designer Config line carries project metadata (plate size, colours,
    // theme) that has no meaning in the openHASP JSONL protocol — it belongs to
    // the design save only. The device receives page and object lines only.
    if (isDesignSave) {
      this.exportData.push({
        "comment": "Designer Config",
        "plate": {
          w: settings.plate.width,
          h: settings.plate.height,
          size_name: settings.plate.size_name,
          primary_color: settings.plate.primary_color,
          secondary_color: settings.plate.secondary_color,
          theme: settings.plate.hasp_theme
        }
      });
    }
    layer.getChildren().forEach(element => {
      if (element instanceof HaspPage) {
        pages.push(element)
      }
    });
    pages.sort(function (a, b) {
      return a.attrs.haspid - b.attrs.haspid;
    });

    // New runtime contract: reusable template definitions precede all page
    // headers. This is separate from HaspTemplatePage.export(), which remains
    // the round-trip/design-save representation. Standard device export has
    // no template page, targetPage, page, or source context in this section.
    if (!isDesignSave) {
      const templates = pages
        .filter(page => page instanceof HaspTemplatePage)
        .sort((a, b) => Number(a.haspid) - Number(b.haspid));
      this._templateDefinitionObjectIds = new Set();
      for (const template of templates) {
        const definition = serializeTemplateDefinitionRecords(template, { templateVisibilityMode });
        this._addValidationErrors(definition.errors, 'template');
        if (definition.errors.length > 0) return [];
        definition.objects.forEach(object => this._templateDefinitionObjectIds.add(Number(object.id)));
        definition.records.forEach(record => this.exportData.push(record));
      }
    } else {
      this._templateDefinitionObjectIds = new Set();
    }

    pages.map(page => {
      // Template pages are represented by definition records above in the
      // standard export; design saves keep the original round-trip page model.
      if (page instanceof HaspTemplatePage && !isDesignSave) return;
      const serializedPageLines = [];
      page.export(page, serializedPageLines, includeHass, isDesignSave);
      const pageLines = isDesignSave ? serializedPageLines : exportSnapshot(serializedPageLines);
      let exportedPageLines = pageLines;
      if (isDesignSave) {
        this._attachDesignerEndpointIds(page, pageLines);
        attachDesignerAuthoringData(page, pageLines);
        attachDesignerPageRoutingSettings(pageLines, connectionRoutingSettings(layer).connection_spacing);
      }
      if (!isDesignSave) {
        // Event/action nodes compile into record.actions; _processActions serializes them.
        this.warnings.push(...attachPageLogicActions(page, pageLines));
        this.warnings.push(...resolveBadgeConnectionWireModel(page, pageLines));
        this._stripUnsupportedObjects(pageLines);
        this._injectCustomComponentMarkers(pageLines);
        exportedPageLines = splitFirmwareConnectionRecords(orderFirmwarePageRecords(pageLines));
      }
      exportedPageLines.forEach(line => this.exportData.push(line));
    })

    // Include script flows only in design saves
    if (isDesignSave) {
      const flowData = exportFlows();
      if (flowData) {
        this.exportData.push({ comment: '[flows]', flows: flowData });
      }
    }

    if (!isDesignSave) {
      this._stripUnlinkedDatasetWidgets();
      this._stripComments();
      this._processDatasets();
      this._processFonts();
      this._processActions();
      this._process3dResources();
      this._stripImageUuids();
      this._stripCustomComponentMetadata();
      this.exportData = splitFirmwareBadgeRecords(this.exportData);
      const checked = enforceFirmwareLineLimit(this.exportData);
      this.exportData = checked.records;
      this._addValidationErrors(checked.errors, 'general');
    }

    // Validate GPIO after all object/template records have reached their
    // firmware shape. Keep invalid records in the result so design saves and
    // diagnostics never silently discard a binding; runtime consumers already
    // receive the blocking validation error through the existing exporter UI.
    this._validateGpioContract(this.exportData);

    return this.exportData;
  }

  _attachDesignerEndpointIds(page, pageLines) {
    attachDesignerEndpointIds(page, pageLines);
  }

  _addValidationError(error, category = 'general') {
    this.validationErrors.push(error);
    this.validationErrorCategories.push(category);
  }

  _addValidationErrors(errors, category = 'general') {
    (errors ?? []).forEach(error => this._addValidationError(error, category));
  }

  _validateGpioContract(records) {
    // Entity references are checked against the GPIO configuration stored with
    // the plate — not live MQTT data, so the result does not depend on whether
    // the plate happens to be connected.
    const metadata = gpioDescriptorFromConfig(this.settings?.plate?.gpio_config);
    const plateLabel = this.settings?.plate?.name ? `plate ${this.settings.plate.name}` : 'the plate';
    for (const record of records ?? []) {
      for (const entity of Object.keys(record?.gpio_actions ?? {})) {
        if (metadata && !metadata.entities.some(item => item.id === entity)) {
          this.warnings.push(`Page ${record.page} GPIO events: entity '${entity}' is not in the GPIO configuration of ${plateLabel}.`);
        }
      }
      if (record?.gpio_bindings === undefined) continue;
      const result = validateGpioBindings(record.gpio_bindings, {
        objectType: record.obj,
        objectConfig: record,
        metadata,
      });
      for (const error of result.errors) this._addValidationError(`GPIO ${record.obj ?? 'object'} id=${record.id}: ${error.message}`, 'gpio');
      for (const warning of result.warnings) this.warnings.push(`GPIO ${record.obj ?? 'object'} id=${record.id}: ${warning.message}`);
      const bytes = firmwareRecordByteLength(record);
      if (bytes > FIRMWARE_MQTT_MAX_JSONL_PAYLOAD_BYTES) {
        this.warnings.push(`GPIO ${record.obj ?? 'object'} id=${record.id}: ${bytes} bytes exceeds the direct MQTT JSONL payload limit of ${FIRMWARE_MQTT_MAX_JSONL_PAYLOAD_BYTES}; use pages.jsonl file upload.`);
      }
    }
    const lineLimit = validateFirmwareJsonlLineLimit(records);
    this._addValidationErrors(lineLimit.errors, 'general');
  }

  _stripUnsupportedObjects(pageLines) {
    this.warnings.push(...stripUnsupportedObjects(pageLines));
  }

  /**
   * Firmware-only: dataset-driven widgets (schema declares a `dataset` prop)
   * with no dataset linked are silently broken on-device — remove them from
   * the plate JSONL and record a warning, rather than exporting a widget with
   * no data source. Never runs for design-save, so the widget is always kept
   * in the project file even before a Dataset is linked.
   */
  _stripUnlinkedDatasetWidgets() {
    for (let i = this.exportData.length - 1; i >= 0; i--) {
      const item = this.exportData[i];
      if (this._templateDefinitionObjectIds?.has(Number(item.id))) continue;
      // Gauge's dataset is optional: manual/legacy Gauges remain valid when
      // no dataset is selected. Chart/Legend retain their required-resource
      // behavior.
      if (item.obj && item.obj !== 'gauge' && hasSchemaProp(item.obj, 'dataset') && !item.dataset) {
        const label = item.obj.charAt(0).toUpperCase() + item.obj.slice(1);
        this.warnings.push(`${label} ${item.id} has no Dataset linked — skipped from export`);
        this.exportData.splice(i, 1);
      }
    }
  }

  /**
   * Firmware-only: `comment` is Designer metadata with no openHASP meaning.
   * Page titles ("Page 0"), per-object notes, the image widget's URL prefix
   * (HaspImage.objectExport) and the custom-component run markers injected by
   * _injectCustomComponentMarkers all travel in it. None of that belongs in the
   * device JSONL. Design saves keep every comment untouched, so nothing is lost
   * from the project file and legacy files stay importable either way.
   *
   * A record whose only key was `comment` (the custom-component markers) would
   * be left as an empty `{}` line, so it is dropped rather than sent.
   */
  _stripComments() {
    for (let i = this.exportData.length - 1; i >= 0; i--) {
      const item = this.exportData[i];
      if (item.comment === undefined) continue;
      delete item.comment;
      if (Object.keys(item).length === 0) this.exportData.splice(i, 1);
    }
  }

  /**
   * Firmware-only: `image` (the ImageResource uuid — see HaspImage.objectExport
   * and src/stores/imageStore.js) is a Designer-internal reference with no
   * firmware equivalent; `src`/`comment` already carry the resolved
   * `L:/filename.png` firmware needs, set independently. Unlike `dataset`,
   * there's no value to translate — the key is simply not part of the wire
   * format, so it's removed rather than rewritten. Design-save keeps it
   * (this only runs for !isDesignSave) so the uuid round-trips across saves.
   */
  _stripImageUuids() {
    for (const item of this.exportData) {
      if (item.image !== undefined) delete item.image;
    }
  }

  /**
   * Firmware-only: custom component ownership is Designer model metadata.
   * It is needed while a component is placed and while a design is saved, but
   * it is not an openHASP JSONL property. Keep it available until the runtime
   * export has finished deriving any component markers, then remove it from
   * every record, including template-definition objects and ordinary page
   * objects.
   */
  _stripCustomComponentMetadata() {
    for (const item of this.exportData) {
      delete item.custom_component_id;
      delete item.custom_component_name;
    }
  }

  /**
   * Firmware-only: validate referenced datasets, prepend
   * {obj:'resource', kind:'dataset', ...} JSONL lines, and resolve UUID →
   * numeric id on widget lines. Strips series from linked charts. Format
   * confirmed against firmware (hasp_dataset.h / hasp_resource.cpp):
   *   {"obj":"resource","kind":"dataset","id":44,"unit":"kWh","type":"power","series":[...]}
   * Same {obj:'resource',kind:...} envelope as _processFonts() — both are
   * dispatched by the same firmware-side Resource Dispatcher.
   * Populates this.validationErrors on failure.
   */
  _processDatasets() {
    const store = useDatasetStore();

    // Collect all dataset UUID references from widget lines
    const referencedUUIDs = new Set();
    for (const item of this.exportData) {
      if (item.dataset && typeof item.dataset === 'string') {
        referencedUUIDs.add(item.dataset);
      }
    }
    if (referencedUUIDs.size === 0) return;

    // Validate: all referenced datasets exist in the library
    const missing = [];
    for (const uuid of referencedUUIDs) {
      if (!store.getDataset(uuid)) missing.push(uuid);
    }
    if (missing.length > 0) {
      this._addValidationError(
        `Dataset${missing.length > 1 ? 's' : ''} not found in library: ${missing.join(', ')}`,
        'dataset',
      );
      return;
    }

    // Collect the actual dataset objects
    const datasets = [...referencedUUIDs].map(uuid => store.getDataset(uuid));

    // Validate: no duplicate numeric ids
    const seenIds = new Map();
    for (const ds of datasets) {
      if (seenIds.has(ds.id)) {
        this._addValidationError(
          `Duplicate dataset id ${ds.id}: used by "${seenIds.get(ds.id)}" and "${ds.name}"`,
          'dataset',
        );
      } else {
        seenIds.set(ds.id, ds.name);
      }
    }
    if (this.validationErrors.length > 0) return;

    // Build UUID → numericId lookup
    const uuidToId = new Map(datasets.map(ds => [ds.uuid, ds.id]));

    // Insert dataset lines at the top, before the first page line
    const datasetLines = datasets.map(ds => ({
      obj: 'resource',
      kind: 'dataset',
      id: ds.id,
      ...(ds.unit ? { unit: ds.unit } : {}),
      ...(ds.type ? { type: ds.type } : {}),
      series: ds.series.map(({ label, color }) => ({ label, color })),
    }));
    this.exportData.splice(0, 0, ...datasetLines);

    // Resolve UUID → numericId on widget lines and strip series from linked charts
    for (const item of this.exportData) {
      if (item.dataset && typeof item.dataset === 'string') {
        item.dataset = uuidToId.get(item.dataset) ?? item.dataset;
        delete item.series;
      }
    }
  }

  /**
   * Firmware-only: rewrite each widget's `font` (the Designer's Font Family
   * selection — a project Font Resource's on-device `file` path, see
   * label.js's `font` property comment) onto `text_font`, and carry
   * `text_font`'s old numeric value (the "Size (px)" field) over to a new
   * `text_size` key — verbatim, with no branching on which kind of string
   * `font` holds. The firmware FontManager alone decides how to resolve it;
   * this method's only job is renaming keys, not validating or registering
   * anything (there is no longer a resource line for fonts at all — see
   * hasp_font_binding.cpp's rewritten resolution order).
   *
   * Same rewrite, second independent slot: gauge's `value_font_resource` →
   * `value_font`, old `value_font` (numeric size) → `value_size`. Firmware's
   * VALUE_FONT attribute is a wholly separate font binding from TEXT_FONT
   * (see hasp_font_binding.h) — same two-key convention, different pair of
   * wire keys.
   *
   * btnmatrix's `font` targets its item_buttons part, which carries the '40'
   * (LV_PART_ITEMS) wire suffix (see btnmatrix.js's exportSuffix and
   * wireFormat.js's toWireKey) — so its text size already exported as
   * `text_font40`, not the bare `text_font` the generic rewrite above targets.
   * Handled as its own branch, keyed by suffix rather than by property name.
   *
   * chart's `font` is different again: it's a genuinely PART-scoped property
   * (x_axis.font / y_axis.font, see chart.js/shared.js's AXIS_STYLE), one
   * independent slot per axis, already wire-suffixed by the generic exporter
   * to e.g. `font60`/`font90` before this method ever runs — never a bare
   * `item.font`. Same two-key rewrite, just applied once per axis suffix.
   * Suffixes are read from the schema itself (getObjectSchema), not
   * hardcoded, so this stays correct if x_axis/y_axis's exportSuffix ever
   * changes again.
   */
  _processFonts() {
    const chartSchema = getObjectSchema('chart');
    for (const item of this.exportData) {
      if (item.obj === 'btnmatrix' && item.font && isProjectFontReference(item.font)) {
        const size = item.text_font40;
        item.text_font40 = item.font;
        item.text_size40 = size;
        delete item.font;
      } else if (item.obj === 'btnmatrix' && item.font) {
        delete item.font;
      } else if (item.obj === 'chart') {
        for (const part of ['x_axis', 'y_axis']) {
          const suffix = chartSchema?.parts?.[part]?.exportSuffix ?? '';
          const fontKey = 'font' + suffix;
          if (!item[fontKey]) continue;
          if (!isProjectFontReference(item[fontKey])) {
            delete item[fontKey];
            continue;
          }
          const sizeKey = 'text_font' + suffix;
          const size = item[sizeKey];
          item[sizeKey] = item[fontKey];
          item['text_size' + suffix] = size;
          delete item[fontKey];
        }
      } else if (item.obj === 'legend') {
        const legendSchema = getObjectSchema('legend');
        for (const part of ['label', 'value']) {
          const suffix = legendSchema?.parts?.[part]?.exportSuffix ?? '';
          const fontKey = 'font' + suffix;
          if (!item[fontKey]) continue;
          if (!isProjectFontReference(item[fontKey])) {
            delete item[fontKey];
            continue;
          }
          const sizeKey = 'text_font' + suffix;
          const size = item[sizeKey];
          item[sizeKey] = item[fontKey];
          item['text_size' + suffix] = size;
          delete item[fontKey];
        }
      } else if (item.obj === 'badge' && item.text_font_resource && isProjectFontReference(item.text_font_resource)) {
        const size = item.text_font;
        item.text_font = item.text_font_resource;
        item.text_size = size;
        delete item.text_font_resource;
      } else if (item.obj === 'badge' && item.text_font_resource) {
        delete item.text_font_resource;
      } else if (item.font && isProjectFontReference(item.font)) {
        const size = item.text_font;
        item.text_font = item.font;
        item.text_size = size;
        delete item.font;
      } else if (item.font) {
        delete item.font;
      }
      if (item.value_font_resource && isProjectFontReference(item.value_font_resource)) {
        const size = item.value_font;
        item.value_font = item.value_font_resource;
        item.value_size = size;
        delete item.value_font_resource;
      } else if (item.value_font_resource) {
        delete item.value_font_resource;
      }
    }
  }

  /**
   * Runtime export: the conversion boundary between the Designer's structured
   * per-event actions and the legacy openHASP `action` wire map. `actions`
   * ({ [event]: { command, resourceId? } } — see ObjectExport) is project
   * data and never reaches the device; what ships is
   *   {"action":{"down":"audio L:/click.wav","up":"audio L:/release.wav"}}
   *
   * Template visibility actions use the semantic `template.show/hide=<id>`
   * contract here. The old embedded-JSONL expansion remains available only
   * through the explicit legacy serializer.
   *
   * Each event resolves independently. An untyped command ("page 2",
   * "backlight on", …) passes through verbatim. A typed `audio.play` command
   * resolves its resource uuid to a local path, the original HTTP(S) URL, or
   * the current filename of a referenced Synth Library resource. Synth data is
   * generated separately by the existing deploy serializer when uploaded.
   *
   * An unresolvable reference (resource deleted, or none picked yet) never
   * emits `audio L:/undefined`: only *that* event is dropped from the device
   * JSONL — every other event on the same widget still exports — and one
   * warning naming that event is recorded, the same non-blocking treatment
   * _stripUnlinkedDatasetWidgets gives a widget with no dataset. If no valid
   * event is left, no empty `action` property is emitted at all. The design
   * package is untouched (this only runs for !isDesignSave), so the action
   * survives internally and reappears once the resource is restored.
   *
   */
  _processActions() {
    const store = useAudioStore();
    const synthStore = useSynthStore();
    let sourcePageId = null;
    const serializeOptions = pageId => ({
      templateVisibilityMode: this.templateVisibilityMode ?? 'semantic',
      sourcePageId: pageId,
      resolveTemplateTarget: templateId => this.layer.getChildren().find(page =>
        page.obj === 'template_page' && Number(page.haspid) === Number(templateId)),
      resolveTarget: (targetPageId, objectId) => this._findExportObject(targetPageId, objectId),
      resolveAudio: uuid => {
        const resource = uuid ? store.getAudio(uuid) : null;
        const synthFilename = resource?.type === AUDIO_SYNTH_RESOURCE_TYPE
          ? synthDeviceFilename(synthStore.getSynth(resource.synthId))
          : null;
        return audioPlaybackCommand(resource, synthFilename);
      },
    });
    const firmwareFeatures = new Set();
    for (const item of this.exportData) {
      sourcePageId = getExportPageContext(sourcePageId, item);
      if (item.gpio_entity_actions) {
        this._processGpioEntityActions(item, serializeOptions(sourcePageId))
          .forEach(feature => firmwareFeatures.add(feature));
      }
      // A page header ({ page } without id) only carries actions of the page
      // itself (its event/action nodes, e.g. swipe gestures).
      if (item.id === undefined && item.page !== undefined && !item.actions) {
        continue;
      }
      const actions = item.actions;
      delete item.actions;
      if (!actions) continue;

      // The source object decides which GPIO Entity actions are firmware bindings.
      const { action, errors, gpioBindings, firmwareFeatures: features = [] } = serializeObjectActions(actions, {
        ...serializeOptions(sourcePageId),
        sourceNode: item.id === undefined ? { obj: 'page' } : item,
      });
      features.forEach(feature => firmwareFeatures.add(feature));
      if (gpioBindings?.events?.length) {
        const current = canonicalizeGpioBindings(item.gpio_bindings) ?? { events: [], state: [] };
        item.gpio_bindings = {
          ...current,
          events: mergeGpioEventBindings(current.events, gpioBindings.events),
        };
      }
      for (const error of errors) {
        const label = item.obj ? item.obj.charAt(0).toUpperCase() + item.obj.slice(1)
          : item.id === undefined ? 'Page' : 'Object';
        this.warnings.push(`${label} ${item.id ?? item.page}: "${error.event}" ${error.message} — that action skipped from export`);
      }
      // Gestures (swipe) have their own firmware attribute; it replaces the
      // page's default `swipe: 1` mapping.
      const { action: eventWire, attributes } = splitGestureWire(action, getObjectSchema(item.obj ?? 'page')?.gestures);
      Object.assign(item, attributes);
      if (Object.keys(eventWire).length > 0) item.action = eventWire;
    }
    if (firmwareFeatures.size > 0) {
      this.warnings.push(`GPIO entity commands use ${[...firmwareFeatures].join(', ')}; firmware without that support ignores those commands (docs/GPIO-LOGIC-FIRMWARE-SPEC.md).`);
    }
  }

  _process3dResources() {
    const store = useModel3dStore();
    const runtimeFilenames = resolveUsedH3dRuntimeFilenames(this.layer, store);

    for (let i = this.exportData.length - 1; i >= 0; i -= 1) {
      const item = this.exportData[i];
      if (item.obj !== '3d' && item.obj !== 'h3d') continue;

      item.obj = '3d';
      const resourceId = item.resource;
      delete item.resource;

      if (!resourceId) {
        this._addValidationError(`3D object ${item.id} has no model resource selected`, 'general');
        delete item.src;
        this.exportData.splice(i, 1);
        continue;
      }

      const model = store.getModel(resourceId);
      if (!model) {
        this._addValidationError(`3D object ${item.id} references missing model resource: ${resourceId}`, 'general');
        delete item.src;
        this.exportData.splice(i, 1);
        continue;
      }

      const filename = runtimeFilenames.get(resourceId);
      if (!filename) {
        this._addValidationError(`3D object ${item.id} could not resolve runtime filename for model "${model.name}"`, 'general');
        delete item.src;
        this.exportData.splice(i, 1);
        continue;
      }

      item.src = `L:/${filename}`;
    }
  }

  /**
   * GPIO entity events of a page (event/action nodes wired to a GPIO entity
   * node) become the page header's `gpio_actions: { <entity>: { <event>:
   * commands } }` — the same shape as `action`. Designer-first: the current
   * firmware publishes GPIO input events but cannot run commands for them, so
   * the export says so (see docs/GPIO-INPUT-ACTIONS-FIRMWARE-SPEC.md).
   * @returns {string[]} firmware features its GPIO entity commands rely on
   */
  _processGpioEntityActions(item, options) {
    const handlersByEntity = item.gpio_entity_actions;
    delete item.gpio_entity_actions;
    const gpioActions = {};
    const firmwareFeatures = new Set();
    for (const [entity, handlers] of Object.entries(handlersByEntity)) {
      // A GPIO source has no firmware bindings: its GPIO Entity actions are entity commands.
      const { action, errors, firmwareFeatures: features = [] } = serializeObjectActions(handlers, { ...options, sourceNode: { obj: 'gpio' } });
      features.forEach(feature => firmwareFeatures.add(feature));
      for (const error of errors) {
        this.warnings.push(`Page ${item.page} GPIO ${entity}: "${error.event}" ${error.message} — that action skipped from export`);
      }
      if (Object.keys(action).length > 0) gpioActions[entity] = action;
    }
    if (Object.keys(gpioActions).length > 0) {
      item.gpio_actions = gpioActions;
      this.warnings.push(`Page ${item.page}: GPIO entity events are exported as gpio_actions; firmware without GPIO input actions ignores them.`);
    }
    return [...firmwareFeatures];
  }

  /**
   * Resolve an object in the page-scoped JSONL export stream. Object records
   * deliberately do not repeat their page number; a {"page": n} header sets
   * the page context for every following object until the next header.
   */
  _findExportObject(pageId, objectId) {
    return findExportObject(this.exportData, pageId, objectId);
  }

  /**
   * Mutates `lines` in-place: wraps consecutive runs of objects sharing the
   * same custom_component_id in start/end comment lines and strips the
   * custom_component_* properties from the firmware JSONL.
   */
  _injectCustomComponentMarkers(lines) {
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.custom_component_id) { i++; continue; }

      const compId   = line.custom_component_id;
      const compName = line.custom_component_name ?? compId;

      // Find the end of this run
      let j = i;
      while (j < lines.length && lines[j].custom_component_id === compId) j++;

      // Strip the tracking fields from the objects themselves
      for (let k = i; k < j; k++) {
        delete lines[k].custom_component_id;
        delete lines[k].custom_component_name;
      }

      // Insert markers
      lines.splice(j, 0, { comment: `[custom:${compName}:${compId}] end` });
      lines.splice(i, 0, { comment: `[custom:${compName}:${compId}] start` });

      // Advance past the markers + the run
      i = j + 2;
    }
  }

  renderYamlEntry(entry) {
    // Pre-rendered strings (backdrop dismiss YAML, etc.)
    if (entry.rendered !== undefined) {
      return Promise.resolve(entry.rendered);
    }
    if (entry.options !== undefined) {
      return Promise.resolve(generateObjectYaml(entry.options));
    }
    return Promise.resolve('');
  }

  /**
   * Native openHASP plate YAML export.
   *
   * This is the file consumed by the openHASP custom component. Its entries
   * are `objects:` with nested `event:` service calls, not standalone HA
   * automations with MQTT triggers.
   */
  hassExport(_settings, layer) {
    this.validationErrors = [];
    this.validationErrorCategories = [];
    const objectIdErrors = validateDesignerObjectIds(layer);
    this._addValidationErrors(objectIdErrors, 'general');
    if (objectIdErrors.length > 0) return Promise.resolve(
      objectIdErrors.map(error => `# ERROR: ${error}`).join('\n') + '\n'
    );

    const pages = layer.getChildren()
      .filter(e => e instanceof HaspPage)
      .sort((a, b) => a.attrs.haspid - b.attrs.haspid);

    // Collect datasets referenced by widgets in this plate.
    const referencedUUIDs = new Set();
    this._walkPageObjects(pages, (node) => {
      if (node.dataset && typeof node.dataset === 'string') {
        referencedUUIDs.add(node.dataset);
      }
    });

    const store = useDatasetStore();
    const validDatasets = [];
    for (const uuid of referencedUUIDs) {
      const ds = store.getDataset(uuid);
      if (!ds) {
        this._addValidationError(`Dataset not found in library: ${uuid}`, 'dataset');
        continue;
      }
      if (!ds.series || ds.series.length === 0) {
        this._addValidationError(`Dataset "${ds.name}" has no series configured`, 'dataset');
        continue;
      }

      let dsOk = true;
      const historyHours = Number(ds.history_hours);
      if (ds.history_hours == null || isNaN(historyHours)) {
        this._addValidationError(`Dataset "${ds.name}": history_hours is not configured`, 'dataset');
        dsOk = false;
      } else if (historyHours < 0.1 || historyHours > 168) {
        this._addValidationError(
          `Dataset "${ds.name}": history_hours ${historyHours} is out of range (0.1–168)`,
          'dataset',
        );
        dsOk = false;
      }

      const interval = Number(ds.interval);
      if (ds.interval == null || isNaN(interval)) {
        this._addValidationError(`Dataset "${ds.name}": interval is not configured`, 'dataset');
        dsOk = false;
      } else if (interval < 30 || interval > 86400) {
        this._addValidationError(
          `Dataset "${ds.name}": interval ${interval}s is out of range (30–86400 seconds)`,
          'dataset',
        );
        dsOk = false;
      }

      ds.series.forEach((series, index) => {
        const label = series.label ? `"${series.label}"` : `#${index + 1}`;
        if (!series.entity) {
          this._addValidationError(
            `Dataset "${ds.name}": series ${label} has no Home Assistant entity configured`,
            'dataset',
          );
          dsOk = false;
        }
      });

      if (dsOk) validDatasets.push(ds);
    }

    const parts = [];
    const datasetsYaml = generateDatasetSectionYaml(validDatasets);
    if (datasetsYaml) parts.push(datasetsYaml);

    // The existing plate generator owns the native objects/event/service
    // syntax. Do not route these entries through the standalone automation
    // generator.
    const objectEntries = pages.flatMap(page =>
      // Configuration per object includes the page's Home Assistant flow graph.
      page.getHassYamlEntries(page, { configOf: getEffectiveHassConfig }).filter(entry => {
        const cfg = entry.options?.hass_config ?? {};
        return (cfg.properties || []).length > 0
            || (cfg.automation || []).length > 0
            || (cfg.events || []).some(e => e.eventType && e.service);
      })
    );

    for (const entry of objectEntries) entry.options.validationErrors = this.validationErrors;
    const rendered = objectEntries.map(entry => this.renderYamlEntry(entry));
    return Promise.all(rendered).then(results => {
      const outputParts = [];
      if (this.validationErrors.length > 0) {
        outputParts.push(this.validationErrors.map(error => `# ERROR: ${error}`).join('\n'));
      }
      outputParts.push(...parts);
      const objectsYaml = results.filter(result => result.trim()).join('\n');
      if (objectsYaml) outputParts.push('objects:\n' + objectsYaml);
      return outputParts.join('\n');
    });
  }

  /**
   * Export standalone Home Assistant automations with MQTT widget triggers.
   * Kept separate from hassExport(), which emits the openHASP integration's
   * native `objects:` YAML format.
   */
  hassAutomationExport(settings, layer) {
    this.validationErrors = [];
    this.validationErrorCategories = [];
    const objectIdErrors = validateDesignerObjectIds(layer);
    this._addValidationErrors(objectIdErrors, 'general');
    if (objectIdErrors.length > 0) return '';
    return generateHomeAssistantAutomationYamlForLayer({
      layer,
      plateName: settings?.plate?.name ?? 'openhasp',
      validationErrors: this.validationErrors,
      configOf: getEffectiveHassConfig,
      pageRulesOf: page => compilePageHass(page)?.pageRules ?? [],
    });
  }

  /**
   * Walk all non-template page objects recursively, calling cb for each node.
   * Skips HaspTemplatePage instances (template pages don't contain dataset widgets).
   */
  _walkPageObjects(pages, cb) {
    const walk = (node) => {
      cb(node);
      node.getChildren?.().forEach(walk);
    };
    for (const page of pages) {
      if (page instanceof HaspTemplatePage) continue;
      page.getChildren().forEach(walk);
    }
  }

}
